
import {
  defineComponent,
  ref,
  computed,
  onBeforeUnmount,
  nextTick,
  watch,
} from "vue";
import SelectData from "@/components/Inputs/interface/IInputSelect";

export default defineComponent({
  name: "InputSelect",
  props: {
    options: {
      type: Array as () => SelectData[],
      default: () => [],
    },
    modelValue: {
      type: [String, Number],
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: "select",
    },
  },
  emits: ["update:modelValue", "change"],
  setup(props, { emit }) {
    const isOpen = ref(false);
    const searchQuery = ref("");
    const dropdownRef = ref<HTMLElement | null>(null);
    const searchInput = ref<HTMLInputElement | null>(null);
    const optionsContainer = ref<HTMLElement | null>(null);
    const isTouched = ref(false);

    const visibleCount = ref(20);
    const increment = 20;

    watch(searchQuery, () => {
      visibleCount.value = 20;
    });

    const selectedValue = computed({
      get: () => props.modelValue,
      set: (newValue: string | number) => {
        emit("update:modelValue", newValue);
      },
    });

    const selectedLabel = computed(() => {
      if (!Array.isArray(props.options)) return "";
      if (
        selectedValue.value === undefined ||
        selectedValue.value === null ||
        selectedValue.value === ""
      )
        return "";
      const selectedOption = props.options.find(
        (option) => String(option.value) === String(props.modelValue)
      );
      return selectedOption ? selectedOption.label : "";
    });

    const filteredOptions = computed(() => {
      return props.options.filter(({ label }) =>
        label?.toLowerCase().includes(searchQuery.value.toLowerCase())
      );
    });

    const displayedOptions = computed(() => {
      let optionsList = props.required
        ? [...filteredOptions.value]
        : [{ value: "", label: " " }, ...filteredOptions.value];

      return optionsList.slice(0, visibleCount.value);
    });

    const borderStyle = computed(() => {
      if (!isTouched.value) return "";
      if (props.required && !selectedValue.value) {
        return "1px solid red";
      } else if (!props.required) {
        return "1px solid green";
      }
      return "";
    });

    const toggleDropdown = () => {
      isOpen.value = !isOpen.value;
      if (isOpen.value) {
        searchQuery.value = "";
        visibleCount.value = 20;
        nextTick(() => {
          searchInput.value?.focus();
        });
      }
    };

    const selectOption = (value: any, label: any) => {
      selectedValue.value = value;
      emit("change", { target: { value } });
      isOpen.value = false;
    };

    const handleClickOutside = (event: Event) => {
      if (
        dropdownRef.value &&
        !dropdownRef.value.contains(event.target as Node)
      ) {
        isOpen.value = false;
      }
    };

    const selectFirstOption = () => {
      if (filteredOptions.value.length > 0) {
        const { value, label } = filteredOptions.value[0];
        selectOption(value, label);
      }
    };

    const onScroll = () => {
      const container = optionsContainer.value;
      if (container) {
        const { scrollTop, clientHeight, scrollHeight } = container;
        if (scrollTop + clientHeight >= scrollHeight - 20) {
          if (visibleCount.value < filteredOptions.value.length) {
            visibleCount.value = Math.min(
              visibleCount.value + increment,
              filteredOptions.value.length
            );
          }
        }
      }
    };

    document.addEventListener("click", handleClickOutside);
    onBeforeUnmount(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      isOpen,
      searchQuery,
      filteredOptions,
      displayedOptions,
      toggleDropdown,
      selectOption,
      selectedLabel,
      dropdownRef,
      searchInput,
      optionsContainer,
      selectFirstOption,
      borderStyle,
      onScroll,
    };
  },
});
